import React, { useState, useEffect, useCallback } from "react";
import {
    Plus,
    Pencil,
    Trash2,
    Package,
    ArrowUpToLine,
    Loader2,
    ExternalLink,
    FolderOpen,
} from "lucide-react";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { EmptyState } from "../shared/EmptyState";
import { PageLoader } from "../shared/LoadingSpinner";
import { StatusBadge } from "../shared/Badge";
import { formatRelativeTime } from "../../lib/utils";
import { toast } from "sonner";

const DEFAULT_COLORS = {
    bureautique: "bg-blue-50 text-blue-700 border-blue-200",
    lecteur_pdf: "bg-red-50 text-red-700 border-red-200",
    navigateur: "bg-cyan-50 text-cyan-700 border-cyan-200",
    securite: "bg-green-50 text-green-700 border-green-200",
    multimedia: "bg-purple-50 text-purple-700 border-purple-200",
    logiciel_metier: "bg-orange-50 text-orange-700 border-orange-200",
    utilitaire: "bg-gray-50 text-gray-700 border-gray-200",
    communication: "bg-indigo-50 text-indigo-700 border-indigo-200",
    autre: "bg-gray-50 text-gray-500 border-gray-200",
};

function CategoryBadge({ category, categories }) {
    const cat = categories.find((c) => c.id === category);
    const label = cat?.label || category;
    const colorClass = DEFAULT_COLORS[category] || "bg-gray-50 text-gray-600 border-gray-200";
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}
            data-testid={`category-badge-${category}`}
        >
            {label}
        </span>
    );
}

// ============ CATEGORIES TAB ============
function CategoriesTab({ categories, onReload }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [label, setLabel] = useState("");
    const [saving, setSaving] = useState(false);

    const handleOpenDialog = (cat = null) => {
        if (cat) {
            setEditing(cat);
            setLabel(cat.label);
        } else {
            setEditing(null);
            setLabel("");
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!label.trim()) return;
        setSaving(true);
        try {
            if (editing) {
                await api.updateSoftwareCategory(editing.id, { label: label.trim() });
                toast.success("Catégorie mise à jour");
            } else {
                await api.createSoftwareCategory({ label: label.trim() });
                toast.success("Catégorie créée");
            }
            setDialogOpen(false);
            onReload();
        } catch (error) {
            const msg = error.response?.data?.detail || "Erreur lors de la sauvegarde";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (cat) => {
        if (!window.confirm(`Supprimer la catégorie "${cat.label}" ?`)) return;
        try {
            await api.deleteSoftwareCategory(cat.id);
            toast.success("Catégorie supprimée");
            onReload();
        } catch (error) {
            const msg = error.response?.data?.detail || "Erreur lors de la suppression";
            toast.error(msg);
        }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">{categories.length} catégories</p>
                <Button onClick={() => handleOpenDialog()} data-testid="add-category-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle catégorie
                </Button>
            </div>

            {categories.length === 0 ? (
                <div className="card">
                    <EmptyState
                        icon={FolderOpen}
                        title="Aucune catégorie"
                        description="Créez des catégories pour organiser vos logiciels"
                    />
                </div>
            ) : (
                <div className="card">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Identifiant</th>
                                    <th>Ordre</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {categories.map((cat) => (
                                    <tr key={cat.id} data-testid={`cat-row-${cat.id}`}>
                                        <td>
                                            <CategoryBadge category={cat.id} categories={categories} />
                                        </td>
                                        <td className="font-mono text-sm text-gray-500">{cat.id}</td>
                                        <td className="text-sm text-gray-500">{cat.order ?? "—"}</td>
                                        <td>
                                            <div className="flex items-center gap-2 justify-end">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleOpenDialog(cat)}
                                                    data-testid={`edit-cat-${cat.id}`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                {cat.id !== "autre" && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDelete(cat)}
                                                        data-testid={`delete-cat-${cat.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-500" />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? "Modifier la catégorie" : "Nouvelle catégorie"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="form-label">Nom de la catégorie *</Label>
                            <Input
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                placeholder="Ex: Développement, CAO/DAO..."
                                autoFocus
                                data-testid="category-label-input"
                            />
                        </div>
                        {!editing && label.trim() && (
                            <p className="text-xs text-gray-400">
                                Identifiant généré : <span className="font-mono">{label.trim().toLowerCase().replace(/\s+/g, "_")}</span>
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={!label.trim() || saving}
                            data-testid="save-category-btn"
                        >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {editing ? "Enregistrer" : "Créer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

// ============ CATALOG TAB ============
function CatalogTab({ software, categories, onReload }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        chocolatey_id: "",
        version: "",
        category: "autre",
        license_required: false,
    });

    const categoryMap = {};
    categories.forEach((c) => { categoryMap[c.id] = c.label; });

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditing(item);
            setFormData({
                name: item.name,
                chocolatey_id: item.chocolatey_id || "",
                version: item.version,
                category: item.category || "autre",
                license_required: item.license_required,
            });
        } else {
            setEditing(null);
            setFormData({ name: "", chocolatey_id: "", version: "", category: "autre", license_required: false });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editing) {
                await api.updateSoftware(editing.id, formData);
                toast.success("Logiciel mis à jour");
            } else {
                await api.createSoftware(formData);
                toast.success("Logiciel créé");
            }
            setDialogOpen(false);
            onReload();
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer ce logiciel du catalogue ?")) return;
        try {
            await api.deleteSoftware(id);
            toast.success("Logiciel supprimé");
            onReload();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    // Group by category
    const grouped = {};
    software.forEach((sw) => {
        const cat = sw.category || "autre";
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(sw);
    });

    const categoryOrder = categories.map((c) => c.id);

    return (
        <>
            <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">{software.length} logiciels au catalogue</p>
                <Button onClick={() => handleOpenDialog()} data-testid="add-software-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                </Button>
            </div>

            {software.length === 0 ? (
                <div className="card">
                    <EmptyState icon={Package} title="Aucun logiciel" description="Ajoutez des logiciels au catalogue" />
                </div>
            ) : (
                <div className="space-y-6">
                    {categoryOrder
                        .filter((cat) => grouped[cat]?.length > 0)
                        .map((cat) => (
                            <div key={cat} className="card" data-testid={`category-section-${cat}`}>
                                <div className="card-header flex items-center gap-2">
                                    <CategoryBadge category={cat} categories={categories} />
                                    <span className="text-xs text-gray-400">({grouped[cat].length})</span>
                                </div>
                                <div className="table-container">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Nom</th>
                                                <th>ID Chocolatey</th>
                                                <th>Version</th>
                                                <th>Licence</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {grouped[cat].map((sw) => (
                                                <tr key={sw.id} data-testid={`software-row-${sw.id}`}>
                                                    <td className="font-medium">{sw.name}</td>
                                                    <td className="font-mono text-sm text-gray-500">{sw.chocolatey_id || "\u2014"}</td>
                                                    <td>{sw.version}</td>
                                                    <td>
                                                        {sw.license_required ? (
                                                            <span className="text-orange-600">Requise</span>
                                                        ) : (
                                                            <span className="text-gray-400">Non</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="flex items-center gap-2 justify-end">
                                                            <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(sw)}>
                                                                <Pencil className="w-4 h-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(sw.id)}>
                                                                <Trash2 className="w-4 h-4 text-red-500" />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editing ? "Modifier le logiciel" : "Ajouter un logiciel"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="form-label">Nom *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ex: Microsoft Office 365"
                                data-testid="software-name"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Catégorie</Label>
                            <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                                <SelectTrigger data-testid="software-category">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="form-label">ID Chocolatey</Label>
                            <Input
                                value={formData.chocolatey_id}
                                onChange={(e) => setFormData({ ...formData, chocolatey_id: e.target.value })}
                                placeholder="Ex: microsoft-office-deployment"
                                data-testid="software-chocolatey"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Version</Label>
                            <Input
                                value={formData.version}
                                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                                placeholder="Ex: 2024"
                                data-testid="software-version"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={formData.license_required}
                                onCheckedChange={(checked) => setFormData({ ...formData, license_required: checked })}
                                data-testid="software-license"
                            />
                            <Label>Licence requise</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                        <Button onClick={handleSave} disabled={!formData.name} data-testid="save-software-btn">
                            {editing ? "Enregistrer" : "Créer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

// ============ REQUESTS TAB ============
function RequestsTab({ categories, onReload }) {
    const { getUserById } = useApp();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [promoteDialog, setPromoteDialog] = useState(null);
    const [promoteForm, setPromoteForm] = useState({ category: "autre", version: "latest", chocolatey_id: "", license_required: false });
    const [promoting, setPromoting] = useState(false);

    useEffect(() => { loadRequests(); }, []);

    const loadRequests = async () => {
        try {
            setLoading(true);
            const data = await api.getSoftwareRequests();
            setRequests(data);
        } catch (err) {
            console.error("Failed to load requests:", err);
        } finally {
            setLoading(false);
        }
    };

    const handlePromote = async () => {
        if (!promoteDialog) return;
        setPromoting(true);
        try {
            await api.promoteSoftwareRequest({ name: promoteDialog.name, download_url: promoteDialog.download_url, ...promoteForm });
            toast.success(`"${promoteDialog.name}" ajouté au catalogue`);
            setPromoteDialog(null);
            loadRequests();
            onReload();
        } catch (err) {
            if (err.response?.status === 409) {
                toast.error(err.response.data.detail);
            } else {
                toast.error("Erreur lors de l'ajout au catalogue");
            }
        } finally {
            setPromoting(false);
        }
    };

    if (loading) return <PageLoader />;

    return (
        <>
            <p className="text-sm text-gray-500 mb-4">
                Logiciels demandés hors catalogue par les utilisateurs. Ajoutez les plus fréquents au catalogue.
            </p>

            {requests.length === 0 ? (
                <div className="card">
                    <EmptyState icon={Package} title="Aucune demande" description="Les logiciels hors catalogue demandés par les utilisateurs apparaîtront ici" />
                </div>
            ) : (
                <div className="space-y-3">
                    {requests.map((req) => (
                        <div key={req.name} className="card" data-testid={`request-${req.name.toLowerCase().replace(/\s/g, "-")}`}>
                            <div className="card-body">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="flex items-center gap-3 mb-1">
                                            <h3 className="text-sm font-semibold">{req.name}</h3>
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                                {req.request_count} {req.request_count > 1 ? "demandes" : "demande"}
                                            </span>
                                        </div>
                                        {req.download_url && (
                                            <a href={req.download_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                                <ExternalLink className="w-3 h-3" />
                                                {req.download_url}
                                            </a>
                                        )}
                                        <div className="mt-3 space-y-1">
                                            {req.tickets.map((t) => {
                                                const requester = getUserById(t.requester_id);
                                                return (
                                                    <div key={t.ticket_id} className="flex items-center gap-2 text-xs text-gray-500">
                                                        <span className="font-mono">{t.ticket_number}</span>
                                                        <span>{requester ? `${requester.first_name} ${requester.last_name}` : "Inconnu"}</span>
                                                        <StatusBadge status={t.status} />
                                                        <span className="text-gray-400">{formatRelativeTime(t.created_at)}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <Button size="sm" onClick={() => { setPromoteDialog(req); setPromoteForm({ category: "autre", version: "latest", chocolatey_id: "", license_required: false }); }} data-testid={`promote-${req.name.toLowerCase().replace(/\s/g, "-")}`}>
                                        <ArrowUpToLine className="w-4 h-4 mr-1" />
                                        Ajouter au catalogue
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!promoteDialog} onOpenChange={(v) => !v && setPromoteDialog(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Ajouter "{promoteDialog?.name}" au catalogue</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="form-label">Nom</Label>
                            <Input value={promoteDialog?.name || ""} readOnly className="bg-gray-50" />
                        </div>
                        <div>
                            <Label className="form-label">Catégorie *</Label>
                            <Select value={promoteForm.category} onValueChange={(v) => setPromoteForm({ ...promoteForm, category: v })}>
                                <SelectTrigger data-testid="promote-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {categories.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="form-label">Version</Label>
                            <Input value={promoteForm.version} onChange={(e) => setPromoteForm({ ...promoteForm, version: e.target.value })} placeholder="latest" data-testid="promote-version" />
                        </div>
                        <div>
                            <Label className="form-label">ID Chocolatey</Label>
                            <Input value={promoteForm.chocolatey_id} onChange={(e) => setPromoteForm({ ...promoteForm, chocolatey_id: e.target.value })} data-testid="promote-chocolatey" />
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox checked={promoteForm.license_required} onCheckedChange={(v) => setPromoteForm({ ...promoteForm, license_required: v })} data-testid="promote-license" />
                            <Label>Licence requise</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPromoteDialog(null)}>Annuler</Button>
                        <Button onClick={handlePromote} disabled={promoting} data-testid="confirm-promote-btn">
                            {promoting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Ajouter au catalogue
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

// ============ MAIN PAGE ============
export function AdminSoftware() {
    const [software, setSoftware] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [swData, catData] = await Promise.all([
                api.getSoftwareCatalog(),
                api.getSoftwareCategories(),
            ]);
            setSoftware(swData);
            setCategories(catData);
        } catch (error) {
            toast.error("Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="admin-software">
            <div className="page-header">
                <h1 className="page-title">Catalogue logiciels</h1>
                <p className="page-subtitle">Gérez le catalogue, les catégories et suivez les demandes hors catalogue</p>
            </div>

            <Tabs defaultValue="catalog" className="mt-4">
                <TabsList className="mb-4">
                    <TabsTrigger value="catalog" className="gap-2" data-testid="tab-catalog">
                        <Package className="w-4 h-4" />
                        Catalogue ({software.length})
                    </TabsTrigger>
                    <TabsTrigger value="categories" className="gap-2" data-testid="tab-categories">
                        <FolderOpen className="w-4 h-4" />
                        Catégories ({categories.length})
                    </TabsTrigger>
                    <TabsTrigger value="requests" className="gap-2" data-testid="tab-requests">
                        <ArrowUpToLine className="w-4 h-4" />
                        Demandes hors catalogue
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="catalog">
                    <CatalogTab software={software} categories={categories} onReload={loadData} />
                </TabsContent>

                <TabsContent value="categories">
                    <CategoriesTab categories={categories} onReload={loadData} />
                </TabsContent>

                <TabsContent value="requests">
                    <RequestsTab categories={categories} onReload={loadData} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
